import { type JSX, useRef } from 'react';

import { type CategoryGroup } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { Arrow } from '@cx-modules/ui';
import { mergePersistentParams } from '@cx-modules/utils';

import { useOutrightsCouponContext } from '../../../../hooks';
import { type Bet } from '../../../../interfaces';
import { type LoadOutrightsCouponArgs } from '../../../../types';
import { type MarketBet } from '../../../_common';
import { useShowMore } from '../../hooks';
import { BetOddButton } from '../BetOddButton';

import {
  BetItem,
  BetItemRow,
  BetName,
  BetsShowMore,
  DefaultBetsGridContainer,
  DefaultBetsGridWrapper,
  OddButtonContainer,
  TableHeaderText,
  TableHeaderWrapper,
} from './style';

const arrowColor = 'var(--oc-primary-blue)';

interface DefaultBetsGridProps {
  bets: MarketBet[];
  categoryGroup?: CategoryGroup;
  marketName: string;
  marketUrl?: string;
  oddsShown: LoadOutrightsCouponArgs['oddsShown'];
  requestUrl: string;
  subEventName: string;
}

export const DefaultBetsGrid = ({
  bets,
  categoryGroup,
  marketName,
  marketUrl,
  oddsShown,
  requestUrl,
  subEventName,
}: DefaultBetsGridProps): JSX.Element => {
  const categoryGroupNs = categoryGroup?.translationFile;

  const { t } = useTranslation(categoryGroupNs ? [categoryGroupNs, 'label'] : ['label']);

  const gridRef = useRef<HTMLDivElement>(null);

  const { handleShowMoreClick, hideShowMoreSection, showMore, showMoreText, visibleBets } = useShowMore({
    bets,
    gridRef,
    maxMobileBets: 4,
    maxTabletBets: 9,
  });

  const translateBetNames = useOutrightsCouponContext(state => state.translateBetNames);

  const itemsPerRow = 3;
  const isOverviewTab = false;

  const markets = (visibleBets || []).reduce(
    (resultArray, item, index) => {
      const rowIndex = Math.floor(index / itemsPerRow);

      if (!resultArray[rowIndex]) {
        resultArray[rowIndex] = [];
      }
      resultArray[rowIndex].push(item);

      return resultArray;
    },
    [] as Array<Bet[]>,
  );

  const numberOfRows = markets.length % 3 === 0 ? markets.length : markets.length + 1;

  return (
    <DefaultBetsGridWrapper ref={gridRef}>
      <TableHeaderWrapper>
        <TableHeaderText>Team</TableHeaderText>
        <TableHeaderText>Best Odds</TableHeaderText>
      </TableHeaderWrapper>
      <DefaultBetsGridContainer numberOfRows={numberOfRows}>
        {(markets || []).map(row => (
          <BetItemRow key={markets.indexOf(row)}>
            {row?.map((bet, idx) => {
              const betNameUrl = marketUrl
                ? mergePersistentParams(
                    `${marketUrl}?selectionName=${bet.betName.toLowerCase().replace(/[ ]/g, '-')}`,
                    requestUrl,
                  )
                : undefined;

              return (
                <BetItem
                  className={isOverviewTab ? 'overview' : ''}
                  itemIndex={idx}
                  key={`${bet.betId}-${bet.betName}`}
                >
                  <BetName className={marketUrl ? '' : 'disabled'} href={betNameUrl}>
                    {translateBetNames ? t(bet.betName) : bet.betName}
                  </BetName>
                  <OddButtonContainer>
                    <BetOddButton bet={bet} marketName={marketName} oddsShown={oddsShown} subEventName={subEventName} />
                  </OddButtonContainer>
                </BetItem>
              );
            })}
          </BetItemRow>
        ))}
      </DefaultBetsGridContainer>
      {hideShowMoreSection && (
        <BetsShowMore
          data-track={`show-more-outrights-button-${marketName.replace(' ', '-')}`}
          onClick={handleShowMoreClick}
        >
          {showMoreText}
          {showMore ? <Arrow colour={arrowColor} up x={7} y={0} /> : <Arrow colour={arrowColor} down x={7} y={-3} />}
        </BetsShowMore>
      )}
    </DefaultBetsGridWrapper>
  );
};
